/**
 * Shift arithmetic for the Center Setup editors (z8nrz7f0vu).
 *
 * Mirrors `cgt-backend/src/resource-center/shift-hours.ts` so the editors can
 * show hours, the overnight flag and a pattern's weekly average AS YOU TYPE,
 * before anything is saved. The server stays the authority — every list it
 * returns carries its own derived `hours` — so this only ever previews an
 * unsaved draft. Keep the two in step.
 */

/** "HH:mm", 24-hour, 00:00–23:59. Midnight is 00:00, never 24:00. */
export const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/

const MINUTES_PER_DAY = 24 * 60

export function minutesOfDay(time: string): number | null {
  if (!TIME_OF_DAY.test(time)) return null
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

/** Start to end in minutes; an end at or before the start is the next day. */
export function spanMinutes(startTime: string, endTime: string): number | null {
  const start = minutesOfDay(startTime)
  const end = minutesOfDay(endTime)
  if (start === null || end === null) return null
  return end > start ? end - start : end + MINUTES_PER_DAY - start
}

export function wrapsMidnight(startTime: string, endTime: string): boolean {
  const start = minutesOfDay(startTime)
  const end = minutesOfDay(endTime)
  return start !== null && end !== null && end <= start
}

/** Net hours: span less the break. Null while the times are incomplete. */
export function netHours(startTime: string, endTime: string, breakMinutes: number): number | null {
  const span = spanMinutes(startTime, endTime)
  return span === null ? null : (span - breakMinutes) / 60
}

/** The same rule set the server applies, so the editor can say why before Save. */
export function shiftTimesProblem(
  startTime: string,
  endTime: string,
  breakMinutes: number,
): string | null {
  const span = spanMinutes(startTime, endTime)
  if (span === null) return 'Start and end must be times of day, e.g. 08:00.'
  if (startTime === endTime) return 'A shift must end at a different time from when it starts.'
  if (!Number.isInteger(breakMinutes) || breakMinutes < 0) {
    return 'The break must be a whole number of minutes, 0 or more.'
  }
  if (breakMinutes >= span) return 'The break must be shorter than the shift.'
  return null
}

/** "8 h", "11.5 h". */
export function formatHours(hours: number): string {
  return `${Math.round(hours * 100) / 100} h`
}

export interface CycleDayHours {
  isOn: boolean
  hours: number
}

/** "N working days per X-day cycle · Y h per week on average". */
export function patternSummary(days: readonly CycleDayHours[]): {
  workingDays: number
  averageWeeklyHours: number
} {
  const on = days.filter(d => d.isOn)
  const cycleHours = on.reduce((sum, d) => sum + d.hours, 0)
  const average = days.length === 0 ? 0 : (cycleHours / days.length) * 7
  return { workingDays: on.length, averageWeeklyHours: Math.round(average * 100) / 100 }
}

/** Longest cycle the server accepts: eight weeks. */
export const MAX_CYCLE_DAYS = 56

/** Sun → Sat, matching cycle day 0 = the Sunday of the start week. */
export const WEEKDAY_LETTERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

/** 0 = Sunday … 6 = Saturday — every day a shift may be worked by default. */
export const ALL_WEEKDAYS: readonly number[] = [0, 1, 2, 3, 4, 5, 6]

/** Distinct weekdays, ascending: the form a shift's list is stored in. */
export function normalizeWeekdays(weekdays: readonly number[]): number[] {
  return [...new Set(weekdays)].sort((a, b) => a - b)
}

/**
 * The weekdays a pattern's cycle day can fall on (z8nrz7f7g8). The cycle
 * starts on a Sunday, so in a cycle of whole weeks day N is always weekday
 * N mod 7. Any other length drifts across the week, so the day lands on all
 * seven in turn.
 */
export function patternDayWeekdays(dayIndex: number, cycleDays: number): number[] {
  return cycleDays % 7 === 0 ? [dayIndex % 7] : [...ALL_WEEKDAYS]
}

/** True when a shift may be worked on every weekday the day can fall on. */
export function shiftFitsDay(
  shiftWeekdays: readonly number[],
  dayIndex: number,
  cycleDays: number,
): boolean {
  return patternDayWeekdays(dayIndex, cycleDays).every(d => shiftWeekdays.includes(d))
}

/** "Mon–Fri", "Every day", "Sun, Sat" — a shift's days, short. */
export function weekdaysLabel(weekdays: readonly number[]): string {
  const days = normalizeWeekdays(weekdays)
  if (days.length === 7) return 'Every day'
  if (days.length === 0) return 'No days'
  // A run of three or more consecutive weekdays reads as a range.
  const contiguous = days.every((d, i) => i === 0 || d === days[i - 1] + 1)
  if (contiguous && days.length >= 3) {
    return `${WEEKDAY_LETTERS[days[0]]}–${WEEKDAY_LETTERS[days[days.length - 1]]}`
  }
  return days.map(d => WEEKDAY_LETTERS[d]).join(', ')
}

/** "Week B · Tue" — how the pattern editor names a cycle day. */
export function cycleDayLabel(dayIndex: number): string {
  return `Week ${String.fromCharCode(65 + Math.floor(dayIndex / 7))} · ${WEEKDAY_LETTERS[dayIndex % 7]}`
}

/**
 * Lay a cycle out as calendar weeks for the editor grid: rows of seven,
 * labelled Week A, Week B… An 8-day cycle is two rows, the second holding one
 * day, which is the honest picture of a rotation that drifts across the week.
 */
export function cycleWeeks<T>(days: readonly T[]): { label: string; days: { index: number; day: T }[] }[] {
  const weeks: { label: string; days: { index: number; day: T }[] }[] = []
  days.forEach((day, index) => {
    const w = Math.floor(index / 7)
    if (!weeks[w]) weeks[w] = { label: `Week ${String.fromCharCode(65 + w)}`, days: [] }
    weeks[w].days.push({ index, day })
  })
  return weeks
}

/**
 * Resize a cycle, keeping the days that survive. New days are OFF — growing a
 * cycle must not silently add working days.
 */
export function resizeCycle<T extends { isOn: boolean }>(
  days: readonly T[],
  length: number,
  blank: () => T,
): T[] {
  const n = Math.max(1, Math.min(MAX_CYCLE_DAYS, Math.floor(length)))
  return Array.from({ length: n }, (_, i) => days[i] ?? blank())
}
